import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";

export type AnswerData = {
  questionId: string;
  answerText?: string | null;
  answerValue?: number | null;
};

export type QuestionData = {
  id: string;
  questionText: string;
  description?: string | null;
  questionType: string;
  required?: boolean;
  scaleMin?: number | null;
  scaleMax?: number | null;
  scaleMinLabel?: string | null;
  scaleMaxLabel?: string | null;
  options?: unknown;
};

interface SurveyResponseViewerProps {
  questions: QuestionData[];
  answers: AnswerData[];
}

export function SurveyResponseViewer({ questions, answers }: SurveyResponseViewerProps) {
  const answersMap = new Map(answers.map((a) => [a.questionId, a]));

  return (
    <div className="space-y-6">
      {questions.map((question, index) => {
        const answer = answersMap.get(question.id);
        const questionOptions = (question.options as Array<{ label: string }>) || [];
        const qType = question.questionType === "text" ? "short_text" : question.questionType;

        return (
          <div key={question.id} className="space-y-3" data-testid={`viewer-question-${index}`}>
            <div>
              <Label className="text-sm font-medium">
                {index + 1}. {question.questionText}
                {question.required && <span className="text-[hsl(var(--alert-red))] ml-1">*</span>}
              </Label>
              {question.description && (
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                  {question.description}
                </p>
              )}
            </div>

            {qType === "short_text" ? (
              <Input
                value={answer?.answerText ?? ""}
                disabled
                className="disabled:opacity-80 disabled:cursor-default"
                data-testid={`viewer-answer-${index}`}
              />
            ) : qType === "long_text" ? (
              <Textarea
                value={answer?.answerText ?? ""}
                disabled
                className="min-h-[100px] disabled:opacity-80 disabled:cursor-default"
                data-testid={`viewer-answer-${index}`}
              />
            ) : qType === "single_choice" && questionOptions.length > 0 ? (
              <RadioGroup
                value={answer?.answerText ?? ""}
                disabled
                className="space-y-2"
                data-testid={`viewer-answer-${index}`}
              >
                {questionOptions.map((opt, optIdx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: survey options may have duplicate labels from user input
                  <div key={optIdx} className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={opt.label}
                      id={`viewer-${question.id}-opt-${optIdx}`}
                      disabled
                      data-testid={`viewer-radio-${index}-${optIdx}`}
                    />
                    <Label
                      htmlFor={`viewer-${question.id}-opt-${optIdx}`}
                      className="text-sm font-normal"
                    >
                      {opt.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            ) : qType === "scale" ? (
              <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                <div className="px-2 relative">
                  <Slider
                    min={question.scaleMin || 1}
                    max={question.scaleMax || 5}
                    step={1}
                    value={[answer?.answerValue ?? question.scaleMin ?? 1]}
                    disabled
                    className="pointer-events-none"
                  />
                  <div className="flex justify-between px-1 mt-2">
                    {Array.from(
                      { length: (question.scaleMax || 5) - (question.scaleMin || 1) + 1 },
                      (_, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: scale tick marks are static and never reordered
                        <div key={i} className="flex flex-col items-center">
                          <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
                          <span className="text-xs text-muted-foreground/60 mt-1">
                            {(question.scaleMin || 1) + i}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm font-medium px-2">
                  <span className="text-muted-foreground">
                    {question.scaleMinLabel || question.scaleMin}
                  </span>
                  <span className="text-primary font-semibold">{answer?.answerValue ?? "-"}</span>
                  <span className="text-muted-foreground">
                    {question.scaleMaxLabel || question.scaleMax}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {answer?.answerText || answer?.answerValue || "-"}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
